import { memo, lazy, Suspense } from 'react'

import {
  Route,
  BrowserRouter as Router,
  Routes,
  Navigate,
  RouteProps,
} from 'react-router-dom'

import { Layout } from 'components'
import { ROUTES } from 'settings/constants'
import PageTracker from 'utils/pageTracking'
import { ReferralCapture } from 'components/ReferralCapture/ReferralCapture'

// Lazy load components for code splitting and better performance
/* PLOP_INJECT_IMPORT */
const Referrals = lazy(() => import('views/Referrals/Referrals'))
const ReferralLanding = lazy(() => import('views/ReferralLanding/ReferralLanding'))
const InvestorAdmin = lazy(() => import('views/InvestorPortal/InvestorAdmin'))
const InvestorPortal = lazy(() => import('views/InvestorPortal/InvestorPortal'))
const GameServers = lazy(() => import('views/GameServers/GameServers'))
const GameServersPaymentSuccess = lazy(() => import('views/GameServersPaymentSuccess/GameServersPaymentSuccess'))
const Community = lazy(() => import('views/Community'))
const CommunityDetail = lazy(() => import('views/CommunityDetail').then(m => ({ default: m.CommunityDetail })))
const Forum = lazy(() => import('views/Forum'))
const LeagueByGame = lazy(() => import('views/LeagueByGame').then(m => ({ default: m.LeagueByGame })))
const LeagueDetail = lazy(() => import('views/LeagueDetail').then(m => ({ default: m.LeagueDetail })))
const MatchRoom = lazy(() => import('views/MatchRoom').then(m => ({ default: m.MatchRoom })))
const Game = lazy(() => import('views/Game'))
const GameDetail = lazy(() => import('views/GameDetail'))
const Hub = lazy(() => import('views/Hub'))
const Explore = lazy(() => import('views/Explore'))
const League = lazy(() => import('views/League'))
const NewsDetail = lazy(() => import('views/NewsDetail'))
const CommentFocus = lazy(() => import('views/CommentFocus').then(m => ({ default: m.CommentFocus })))
const Notifications = lazy(() => import('views/Notifications'))
const PrivacyPolicy = lazy(() => import('views/PrivacyPolicy'))
const TermsOfService = lazy(() => import('views/TermsOfService'))
const Profile = lazy(() => import('views/Profile'))
const Store = lazy(() => import('views/Store'))
const Team = lazy(() => import('views/Team'))
const TeamDetail = lazy(() => import('views/TeamDetail/TeamDetail').then(m => ({ default: m.TeamDetail })))
const TeamStratBoard = lazy(() => import('views/TeamStratBoard/TeamStratBoard').then(m => ({ default: m.TeamStratBoard })))
const Tournament = lazy(() => import('views/Tournament'))
const TournamentDetail = lazy(() => import('views/TournamentDetail').then(m => ({ default: m.TournamentDetail })))
const TournamentBracketUI = lazy(() => import('views/TournamentDetail').then(m => ({ default: m.TournamentBracketUI })))
const TournamentOverview = lazy(() => import('views/TournamentDetail/TournamentOverview'))
const TournamentAbout = lazy(() => import('views/TournamentDetail/TournamentAbout'))
const TournamentGroupPlay = lazy(() => import('views/TournamentDetail/TournamentGroupPlay'))
const RegionBuilder = lazy(() => import('views/TournamentDetail/RegionBuilder/RegionBuilder'))
const UserActivate = lazy(() => import('views/UserActivate'))
const StreamDashboard = lazy(() => import('views/Streaming').then(m => ({ default: m.StreamDashboard })))
const StreamLivePage = lazy(() => import('views/Streaming').then(m => ({ default: m.StreamLivePage })))
const StreamBrowsePage = lazy(() => import('views/Streaming').then(m => ({ default: m.StreamBrowsePage })))
const AdminLeagueManager = lazy(() => import('components/AdminLeagueManager/AdminLeagueManager').then(m => ({ default: m.AdminLeagueManager })))
const AdminTeamManager = lazy(() => import('components/AdminTeamManager/AdminTeamManager').then(m => ({ default: m.AdminTeamManager })))
const AdminUserLocations = lazy(() => import('components/AdminTeamManager/AdminUserLocations'))
const LeaguePlace = lazy(() => import('views/LeaguePlace/LeaguePlace').then(m => ({ default: m.LeaguePlace })))
const LeagueManagement = lazy(() => import('components/LeagueManagement/LeagueManagement').then(m => ({ default: m.LeagueManagement })))
const DePIN = lazy(() => import('views/DePIN'))
const Token = lazy(() => import('views/Token'))
const GameServer = lazy(() => import('views/GameServer'))
const Nodes = lazy(() => import('views/Nodes'))
const Studios = lazy(() => import('views/Studios'))
const Wallet = lazy(() => import('views/Wallet/Wallet'))
const EmailPreferences = lazy(() => import('views/EmailPreferences').then(m => ({ default: m.EmailPreferences })))
const PublicStatsPage = lazy(() => import('views/PublicStats').then(m => ({ default: m.PublicStatsPage })))
const PublicBracketPage = lazy(() => import('views/PublicBracket').then(m => ({ default: m.PublicBracketPage })))
const BracketMatchRoomPage = lazy(() => import('views/BracketMatchRoom').then(m => ({ default: m.BracketMatchRoomPage })))
const CommunityBracketEditor = lazy(() => import('views/CommunityBracketEditor').then(m => ({ default: m.CommunityBracketEditor })))
const LanQueue = lazy(() => import('views/LanQueue/LanQueue'))
const QueueStats = lazy(() => import('views/QueueStats').then(m => ({ default: m.QueueStats })))
const QueueMatchDetail = lazy(() => import('views/QueueMatchDetail').then(m => ({ default: m.QueueMatchDetail })))
const TournamentMatchDetail = lazy(() => import('views/TournamentMatchDetail').then(m => ({ default: m.TournamentMatchDetail })))

export type RouteType = {
  hasRightNav: boolean
  fullWidth?: boolean
  element: React.FC<any>
}

const routes: (RouteType & Omit<RouteProps, 'element'>)[] = [
  /* PLOP_INJECT_ROUTE */
  {
    path: ROUTES.LanQueue,
    hasRightNav: false,
    fullWidth: true,
    element: LanQueue,
  },
  { path: ROUTES.InvestorAdmin, hasRightNav: false, fullWidth: true, element: InvestorAdmin },
  { path: ROUTES.Investors, hasRightNav: false, fullWidth: true, element: InvestorPortal },
  {
    path: ROUTES.PublicStats,
    hasRightNav: false,
    element: PublicStatsPage,
  },
  {
    path: ROUTES.PublicBracket,
    hasRightNav: false,
    fullWidth: true,
    element: PublicBracketPage,
  },
  {
    path: ROUTES.BracketMatchRoom,
    hasRightNav: false,
    fullWidth: true,
    element: BracketMatchRoomPage,
  },
  {
    path: ROUTES.QueueStats,
    hasRightNav: false,
    fullWidth: true,
    element: QueueStats,
  },
  {
    path: ROUTES.DePIN,
    hasRightNav: false,
    element: DePIN,
  },
  {
    path: ROUTES.Nodes,
    hasRightNav: false,
    element: Nodes,
  },
  {
    path: ROUTES.Studios,
    hasRightNav: false,
    element: Studios,
  },
  {
    path: ROUTES.Token,
    hasRightNav: false,
    element: Token,
  },
  {
    path: ROUTES.GameServer,
    hasRightNav: false,
    element: GameServer,
  },
  {
    path: ROUTES.GameServerGame,
    hasRightNav: false,
    element: GameServer,
  },
  {
    path: ROUTES.GameServers,
    hasRightNav: false,
    fullWidth: true,
    element: GameServers,
  },
  {
    path: ROUTES.GameServersPaymentSuccess,
    hasRightNav: false,
    element: GameServersPaymentSuccess,
  },
  {
    path: ROUTES.PrivacyPolicy,
    hasRightNav: false,
    element: PrivacyPolicy,
  },
  {
    path: ROUTES.TermsOfService,
    hasRightNav: false,
    element: TermsOfService,
  },
  { path: ROUTES.NewsDetail, hasRightNav: true, element: NewsDetail },
  { path: ROUTES.CommentFocus, hasRightNav: true, element: CommentFocus },
  // Registered before TournamentDetail for readability; React Router v6 ranks by
  // specificity, so the deeper /match/:matchId path is never shadowed by
  // /tournament/:tournamentId (which only matches its own child segments).
  {
    path: ROUTES.TournamentMatchDetail,
    hasRightNav: false,
    fullWidth: true,
    element: TournamentMatchDetail,
  },
  {
    path: ROUTES.TournamentDetail,
    hasRightNav: false,
    fullWidth: true,
    element: TournamentDetail,
    children: (
      <>
        <Route index element={<Navigate to={'Overview'} replace={true} />} />
        <Route path="Overview" element={<TournamentOverview />}></Route>
        <Route path="About" element={<TournamentAbout />}></Route>
        <Route path="Groups" element={<TournamentGroupPlay />}></Route>
        <Route path="Bracket" element={<TournamentBracketUI />}></Route>
        <Route path="Regions" element={<RegionBuilder />}></Route>
      </>
    ),
  },
  { path: ROUTES.Tournament, hasRightNav: false, fullWidth: true, element: Tournament },
  { path: ROUTES.Team, hasRightNav: false, fullWidth: true, element: Team },
  { path: ROUTES.TeamDetail, hasRightNav: false, fullWidth: true, element: TeamDetail },
  { path: ROUTES.TeamStratBoard, hasRightNav: false, fullWidth: true, element: TeamStratBoard },
  { path: ROUTES.Community, hasRightNav: false, fullWidth: true, element: Community },
  { path: ROUTES.CommunityDetail, hasRightNav: false, fullWidth: true, element: CommunityDetail },
  // Authenticated only: /community/* is not in Layout's logged-out allowlist.
  { path: ROUTES.CommunityBracketEditor, hasRightNav: false, fullWidth: true, element: CommunityBracketEditor },

  {
    path: ROUTES.Profile,
    hasRightNav: false,
    fullWidth: true,
    element: Profile,
    children: (
      <>
        <Route path="stats" element={<Profile />}></Route>
        <Route path="shouts" element={<Profile />}></Route>
        <Route path="games" element={<Profile />}></Route>
        <Route path="competitive" element={<Profile />}></Route>
        <Route path="history" element={<Profile />}></Route>
        <Route path="stream" element={<Profile />}></Route>
        <Route path="requests" element={<Profile />}></Route>
      </>
    ),
  },
  {
    path: ROUTES.Wallet,
    hasRightNav: false,
    fullWidth: true,
    element: Wallet,
  },
  {
    path: ROUTES.EmailPreferences,
    hasRightNav: false,
    element: EmailPreferences,
  },
  {
    path: ROUTES.StreamDashboard,
    hasRightNav: false,
    fullWidth: true,
    element: StreamDashboard,
  },
  {
    path: ROUTES.StreamLive,
    hasRightNav: false,
    fullWidth: true,
    element: StreamLivePage,
  },
  {
    path: ROUTES.StreamBrowse,
    hasRightNav: false,
    fullWidth: true,
    element: StreamBrowsePage,
  },

  { path: ROUTES.UserActivate, hasRightNav: false, element: UserActivate },
  { path: ROUTES.League, hasRightNav: false, fullWidth: true, element: League },
  { path: ROUTES.LeagueByGame, hasRightNav: false, fullWidth: true, element: LeagueByGame },
  { path: ROUTES.LeaguePlace, hasRightNav: false, fullWidth: true, element: LeaguePlace },
  { path: ROUTES.LeagueDetail, hasRightNav: false, fullWidth: true, element: LeagueDetail },
  { path: ROUTES.AdminLeagues, hasRightNav: false, element: AdminLeagueManager },
  { path: ROUTES.AdminLeagueManage, hasRightNav: false, element: LeagueManagement },
  { path: ROUTES.AdminLeagueTeams, hasRightNav: false, element: LeagueManagement },
  { path: ROUTES.AdminTeams, hasRightNav: false, element: AdminTeamManager },
  { path: ROUTES.AdminUserLocations, hasRightNav: false, element: AdminUserLocations },
  { path: ROUTES.LeagueManagement, hasRightNav: false, element: LeagueManagement },
  { path: ROUTES.MatchRoom, hasRightNav: false, element: MatchRoom },
  { path: ROUTES.QueueMatchDetail, hasRightNav: false, fullWidth: true, element: QueueMatchDetail },
  {
    path: ROUTES.GameDetail,
    hasRightNav: false,
    fullWidth: true,
    element: GameDetail,
    children: (
      <>
        <Route path="Leaderboard" element={<GameDetail />}></Route>
        <Route path="Shouts" element={<GameDetail />}></Route>
        {/* Legacy: keep Competitive as an alias so old bookmarks still resolve;
            GameDetail re-maps it to the Leaderboard tab on mount. */}
        <Route path="Competitive" element={<GameDetail />}></Route>
        <Route path="Requests" element={<GameDetail />}></Route>
      </>
    ),
  },
  { path: ROUTES.Forum, hasRightNav: false, element: Forum },
  { path: ROUTES.Game, hasRightNav: false, fullWidth: true, element: Game },
  { path: ROUTES.Hub, hasRightNav: false, fullWidth: true, element: Hub },
  { path: ROUTES.Explore, hasRightNav: false, fullWidth: true, element: Explore },
  { path: ROUTES.Notifications, hasRightNav: false, fullWidth: true, element: Notifications },
  { path: ROUTES.Store, hasRightNav: false, element: Store },
  { path: ROUTES.Referrals, hasRightNav: false, fullWidth: true, element: Referrals },
  { path: ROUTES.ReferralLanding, hasRightNav: false, fullWidth: true, element: ReferralLanding },
]

const PublicRoutes = () => {
  return (
    <Router>
      <PageTracker />
      <ReferralCapture />
      <Suspense fallback={
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: '#0a0a0a'
        }}>
          <div style={{
            color: '#02f305',
            fontSize: '1.2rem',
            fontWeight: 600
          }}>
            Loading...
          </div>
        </div>
      }>
        <Routes>
          {routes.map((route) => {
            const Component = route.element

            return (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <Layout hasRightNav={route.hasRightNav} fullWidth={route.fullWidth}>
                    <Component />
                  </Layout>
                }
                children={route.children}
              />
            )
          })}

          <Route path={ROUTES.Home} element={<Navigate to={ROUTES.Hub} replace />} />
          <Route path="*" element={<Navigate to={ROUTES.Hub} />} />
        </Routes>
      </Suspense>
    </Router>
  )
}

export default memo(PublicRoutes)
